import './Dialog.scss'

import classNames from 'classnames'
import type { FocusTargetOrFalse } from 'focus-trap'
import FocusTrap from 'focus-trap-react'
import KeyCode from 'lib/util/KeyCode'
import React, { ReactNode, useEffect, useRef } from 'react'
import ReactDom from 'react-dom'

import DialogBody from './DialogBody'
import DialogFooter from './DialogFooter'
import DialogHeader from './DialogHeader'
import DialogTitle from './DialogTitle'

export interface DialogProps {
  children: ReactNode
  className?: string
  focusTrap?: boolean
  initialFocus?: FocusTargetOrFalse
  onRequestClose?: ()=> void
  type?: 'warning' | 'success' | 'default'
}

function Dialog(props: DialogProps): JSX.Element {
  const {
    className,
    children,
    focusTrap,
    initialFocus,
    onRequestClose,
    type = 'default',
    ...otherProps
  } = props
  const mergedClassName = classNames('three-sixty-dialog', `three-sixty-dialog--${type}`, className)
  const dialogEl = useRef(null)

  function handleMousedownOutside(event: MouseEvent): void {
    if (dialogEl && dialogEl.current && !dialogEl.current.contains(event.target as Node)) {
      onRequestClose && onRequestClose()
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === KeyCode.ESC) {
      onRequestClose && onRequestClose()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleMousedownOutside)
    document.addEventListener('keydown', handleKeyDown)

    return function cleanup() {
      document.removeEventListener('mousedown', handleMousedownOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  })

  return ReactDom.createPortal(
    <FocusTrap
      active={!!focusTrap}
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        initialFocus
      }}
    >
      <div className="three-sixty-dialog-mask">
        <div ref={dialogEl} className={mergedClassName} {...otherProps}>
          {children}
        </div>
      </div>
    </FocusTrap>
    ,
    document.getElementById('portals')
  )
}

Dialog.Header = DialogHeader
Dialog.Title = DialogTitle
Dialog.Body = DialogBody
Dialog.Footer = DialogFooter

export default Dialog
